import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import assetsManager from "../assets/assetsmanager/assestsmanager";
import Pagination from "../component/Pagination";
import UploadPdfModal from "../component/UploadPdfModal";
import EditPdfModal from "../component/EditPdfModal";
import AlertModal from "../component/AlertModal";
import { deletePdf } from "../hooks/deletePdf";
import { AppContext } from "../context/context";

import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const Pdf = () => {
  //static messages for modals
  const notEditableAlertMessage =
    "You can't edit this pdf. You can delete and upload another PDF. Do you want to delete this pdf ?";
  const deleteAlertMessage = "Are you sure you want to delete?";
  //global states
  const { state, fetchDocuments } = useContext(AppContext);
  const { pdfData } = state;

  //modal states
  const [editAlertModalOpen, setEditAlertModalOpen] = useState(false);
  const [deleteAlertModalOpen, setDeleteAlertModalOpen] = useState(false);
  const [uploadPdfModalOpen, setUploadPdfModalOpen] = useState(false);
  const [editPdfModalOpen, setEditPdfModalOpen] = useState(false);

  //pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  //table loading
  const [loading, setLoading] = useState(true);

  //table data
  const [pdfDataLocal, setPdfDataLocal] = useState(pdfData);

  //data states
  const [searchInput, setSearchInput] = useState("");
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);

  //handle pagination
  const handleChangePage = (page) => {
    setPage(page);
  };

  //handle search input
  const handleChangeSearchInput = (e) => {
    setSearchInput(e.target.value);
    handleSearchPdf(e.target.value);
    setPage(0);
  };

  //handle search functionality
  const handleSearchPdf = (input) => {
    let filteredData = pdfData?.filter((item) => {
      return (
        item?.name?.toLowerCase()?.includes(input?.toLowerCase()) ||
        item?.url?.toLowerCase()?.includes(input?.toLowerCase())
      );
    });
    setPdfDataLocal(filteredData);
  };

  //handle upload modal open
  const handleUpload = () => {
    setUploadPdfModalOpen(true);
  };

  //handle edit pdf
  const handleEditPdf = (pdf) => {
    setSelectedPdf(pdf);
    let regex = /(?:http:\/|https:\/|\/media\/)/;
    if (regex.test(pdf.name)) {
      setEditAlertModalOpen(true);
    } else {
      setEditPdfModalOpen(true);
    }
  };

  //handle open pdf
  const handleOpenPdf = (url) => {
    window.open(url, "_blank");
  };

  //handle delete pdf
  const handleDeletePdf = (pdf) => {
    setDeleteAlertModalOpen(true);
    setSelectedPdf(pdf);
  };

  //fetch pdf data
  const getData = async () => {
    setLoading(true);
    await fetchDocuments(state?.activeTab);
    setLoading(false);
  };

  //handle cancel delete pdf in modal
  const handleDeleteNo = () => {
    setDeleteAlertModalOpen(false);
    setEditAlertModalOpen(false);
    setSelectedPdf(null);
  };

  //handle delte pdf in modal
  const handleDeleteYes = async () => {
    toast.loading("loading...");
    try {
      let data = await deletePdf(selectedPdf?.name, state.activeTab);
      setEditAlertModalOpen(false);
      setDeleteAlertModalOpen(false);
      toast.dismiss();
      if (data.status === "PDF deleted successfully.") {
        toast.success(data.status);
        fetchDocuments(state?.activeTab);
        setPage(0);
      } else {
        toast.error(data.status);
      }
    } catch (err) {
      console.log("Error: ", err);
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    const worksheetData = pdfDataLocal.map((pdf) => ({
      "PDF Name": pdf.name,
      "PDF Path": pdf.url
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PDF List");

    XLSX.writeFile(workbook, "pdf_list.xlsx");
  };

  // Export to PDF - Fixed version
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text("List of PDFs", 14, 20);
      
      // Prepare table data
      const tableColumn = ["PDF Name", "PDF Path"];
      const tableRows = pdfDataLocal.map((pdf) => [
        pdf.name || "",
        pdf.url || ""
      ]);

      // Add table using autoTable
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 30,
        styles: { 
          fontSize: 10,
          cellPadding: 3,
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        columnStyles: {
          0: { cellWidth: 60 }, // PDF Name column
          1: { cellWidth: 120 } // PDF Path column
        },
        headStyles: {
          fillColor: [71, 85, 105], // Gray color for header
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245] // Light gray for alternate rows
        },
        margin: { left: 14, right: 14 },
        tableWidth: 'auto'
      });

      // Save the PDF
      doc.save("pdf_list.pdf");
      toast.success("PDF exported successfully!");
      
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF. Please try again.");
    }
  };

  //set pdf data in local state
  useEffect(() => {
    setPdfDataLocal(pdfData);
  }, [pdfData]);

  //get pdf data on page load
  useEffect(() => {
    getData();
  }, [state?.activeTab]);

  useEffect(() => {
    const handleClickOutside = () => setExportDropdownOpen(false);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="h-[calc(100%_-_56px)] pb-2 mt-2">
      <div className="flex items-center justify-between">
        <p className="text-lg font-medium text-[#424242]">List of PDFs</p>

        <div className="flex items-center gap-2">
          {/* Upload Button */}
          <button
            className="flex items-center justify-center gap-1.5 h-8 rounded-md bg-[#C7222A] text-white text-sm w-[7.5rem]"
            onClick={handleUpload}
          >
            <p>Upload PDF</p>
            <img className="h-[14px]" src={assetsManager?.uploadIcon} alt="" />
          </button>

          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation(); // prevent outside click listener from closing it instantly
                setExportDropdownOpen(!exportDropdownOpen);
              }}
              className="flex items-center justify-center gap-1.5 h-8 rounded-md bg-gray-700 text-white text-sm w-[7.5rem]"
            >
              Export ▼
            </button>

            {exportDropdownOpen && (
              <div className="absolute right-0 mt-1 w-32 bg-white border rounded shadow-lg z-50">
                <button
                  onClick={() => {
                    handleExportExcel();
                    setExportDropdownOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                >
                  Excel
                </button>
                <button
                  onClick={() => {
                    handleExportPDF();
                    setExportDropdownOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                >
                  PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 h-[calc(100%_-_32px)]">
        <div className="w-full relative">
          <img
            className="absolute top-1/2 transform translate-y-[-50%] left-[14px] h-[13px]"
            src={assetsManager.searchIcon}
            alt=""
          />
          <input
            className="lg:w-[24rem] md:min-w-[0] rounded-[4px] p-2 px-9 focus:outline-none bg-[#EFEFEF] text-sm"
            placeholder="Search PDF"
            onChange={handleChangeSearchInput}
            value={searchInput}
          />
        </div>
        <div className="h-[calc(100%_-_36px)] overflow-auto relative flex flex-col justify-between">
          <table
            className={`w-full table-fixed min-w-[500px] ${
              pdfDataLocal?.length === 0 && "sticky top-0"
            }`}
          >
            <thead className="sticky top-0">
              <tr>
                <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                  PDF Name
                </th>
                <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                  Path
                </th>
                <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {pdfDataLocal?.length > 0 &&
                pdfDataLocal
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, i) => {
                    return (
                      <tr className="border-b-[2px] border-[#EAECF0]" key={i}>
                        <td className=" text-sm p-3 text-start">
                          <p
                            title={item.name}
                            className="text-ellipsis text-nowrap overflow-hidden"
                          >
                            {item?.name}
                          </p>
                        </td>
                        <td className=" text-sm p-3 text-start text-[#1C4EB2]">
                          <p
                            className="text-ellipsis text-nowrap overflow-hidden cursor-pointer w-[90%]"
                            title={item?.url}
                            onClick={() => handleOpenPdf(item?.url)}
                          >
                            {item?.url}
                          </p>
                        </td>
                        <td className=" text-sm p-3 text-start">
                          <div className="flex gap-1 h-full">
                            <div
                              onClick={() => handleEditPdf(item)}
                              className="flex justify-center p-2  cursor-pointer h-full"
                            >
                              <img
                                className="h-[13px] w-[17px]"
                                src={assetsManager.edit}
                                alt=""
                              />
                            </div>
                            <div
                              onClick={() => handleOpenPdf(item?.url)}
                              className="flex justify-center p-2  cursor-pointer h-full"
                            >
                              <img
                                className="h-[13px] w-[14px]"
                                src={assetsManager.eye}
                                alt=""
                              />
                            </div>
                            <div
                              onClick={() => handleDeletePdf(item)}
                              className="flex items-center justify-center  p-2 cursor-pointer h-full"
                            >
                              <img
                                className="h-[13px] w-[17px]"
                                src={assetsManager.deleteIcon}
                                alt=""
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
          {pdfDataLocal?.length === 0 && (
            <div className=" h-[calc(100%_-_36px)] flex items-center justify-center">
              {loading ? (
                "Loading..."
              ) : (
                <img
                  className="h-2/3 min-h-24 max-h-48"
                  src={assetsManager.emptyIllustration}
                  alt=""
                />
              )}
            </div>
          )}
        </div>
        <Pagination
          data={pdfDataLocal}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
        />
      </div>
      {uploadPdfModalOpen && (
        <UploadPdfModal setUploadPdfModalOpen={setUploadPdfModalOpen} />
      )}
      {deleteAlertModalOpen && (
        <AlertModal
          alertMessage={deleteAlertMessage}
          handleYes={handleDeleteYes}
          handleNo={handleDeleteNo}
        />
      )}
      {editAlertModalOpen && (
        <AlertModal
          alertMessage={notEditableAlertMessage}
          handleYes={handleDeleteYes}
          handleNo={handleDeleteNo}
        />
      )}
      {editPdfModalOpen && (
        <EditPdfModal
          setEditPdfModalOpen={setEditPdfModalOpen}
          selectedPdf={selectedPdf}
        />
      )}
    </div>
  );
};

export default Pdf;